import {useLingui} from '@lingui/react/macro';
import {Svg, Path} from 'react-native-svg';
import {useSelector} from 'react-redux';
import {useRef, useEffect, Fragment} from 'react';
import {StyleSheet, View, Text, Pressable, ScrollView} from 'react-native';
import {FolderBreadcrumbsItem} from 'interface/stacks/FolderBreadcrumbsItem';
import {useDragDropTarget} from 'interface/hooks/useDragDropTarget';
import {useModalShare} from 'interface/hooks/useModalShare';
import {DropFrame} from 'interface/base/DropFrame';
import {Link} from 'extensions/navigation/components/Link';
import {theme} from 'config/themes';
import {colors, vectors} from 'config/styles';
import {isTouch, isNative} from 'utils/platform';
import {selectors} from 'store/slices/files';

export interface FolderBreadcrumbsProps {
  id: string;
  search?: string;
  minimal?: boolean;
}

export function FolderBreadcrumbs(props: FolderBreadcrumbsProps) {
  const {t} = useLingui();
  const list = useRef<ScrollView>(null);
  const dndRef = useRef<View>(null);
  const folder = useSelector(selectors.getItem(props.id));
  const hasTouch = isTouch();
  const isTrash = props.id === 'trash';
  const isRecent = props.id === 'recent';
  const isFollowing = props.id === 'following';
  const isSearch = !!props.search;
  const isVirtual = isTrash || isRecent || isFollowing || isSearch;
  const iconSize = hasTouch ? 18 : 16;
  const iconRoot = vectors.icons.home;
  const hasDepth = folder && folder.hierarchy && folder.hierarchy.length;

  const root = isTrash
    ? {id: 'trash', name: t`Trash`}
    : isRecent
      ? {id: 'recent', name: 'Recent'}
      : isFollowing
        ? {id: 'following', name: 'Shared'}
        : {id: 'myfiles', name: 'My Files'};

  const classes = {
    outer: [
      styles.outer,
      hasTouch && styles.outerLarge,
    ],
    root: [
      hasTouch ? styles.rootLarge : styles.root,
    ],
    paths: [
      styles.paths,
      hasTouch && styles.pathsLarge,
    ],
    text: [
      styles.text,
      hasTouch && styles.textLarge,
      isVirtual && styles.textSpecial,
    ],
    iconHome: [
      styles.iconHome,
      hasDepth && styles.iconHomeDepth,
    ],
    buttonShare: [
      styles.buttonShare,
      hasTouch && styles.buttonShareTouch,
    ],
  };

  const url = folder ? folder.url : undefined;
  const name = folder ? folder.name : undefined;

  const [showShare] = useModalShare([props.id], name, url);
  const {isDropping} = useDragDropTarget({
    ref: dndRef,
    targetId: root.id,
    targetName: root.name,
  });

  // Scroll to end when folder changes
  useEffect(() => {
    list.current.scrollToEnd({animated: true});
  }, [list, folder]);

  return (
    <View style={classes.outer}>
      {isSearch &&
        <Text style={classes.text}>
          {t`Showing results for “${props.search}”`}
        </Text>
      }
      {isRecent &&
        <Text style={classes.text}>
          {t`Recent files`}
        </Text>
      }
      {isTrash &&
        <Text style={classes.text}>
          {t`Trash folder`}
        </Text>
      }
      {isFollowing &&
        <Text style={classes.text}>
          {t`Shared with me`}
        </Text>
      }
      <ScrollView horizontal ref={list} style={classes.root}>
        {!isSearch && !isVirtual &&
          <Link style={styles.link} to={`/folder/${root.id}`}>
            <View ref={dndRef} style={styles.iconWrapper}>
              <Svg viewBox="0 0 20 20" style={classes.iconHome} width={iconSize} height={iconSize}>
                <Path d={iconRoot} fill={colors.neutral.mid} fillOpacity={0.5}/>
              </Svg>
              {!hasDepth &&
                <Text style={classes.text}>
                  {root.name}
                </Text>
              }
              {isDropping && <DropFrame stroke="small"/>}
            </View>
          </Link>
        }
        {!isSearch && folder && folder.hierarchy &&
          <View style={classes.paths}>
            {[[root.id, root.name], ...folder.hierarchy].map((item, i, paths) => {
              const first = i === 0;
              const last = i === paths.length - 1;
              return (
                <Fragment key={i}>
                  <View style={[styles.item, isNative() && styles.itemNative]}>
                    {!first &&
                      <FolderBreadcrumbsItem active={last} id={item[0]} name={item[1]}/>
                    }
                    {!last &&
                      <Svg style={styles.divider} viewBox="0 0 12 18" width={8} height={12}>
                        <Path d={vectors.icons.right} fill={colors.neutral.mid} fillOpacity={0.5}/>
                      </Svg>
                    }
                    {last && !first &&
                      <Pressable
                        onPress={showShare}
                        accessibilityRole="button"
                        accessibilityLabel={t`Share this folder`}>
                        <View style={classes.buttonShare}>
                          <Text style={styles.textShare}>
                            {t`Share this folder`}
                          </Text>
                        </View>
                      </Pressable>
                    }
                  </View>
                </Fragment>
              )
            })}
          </View>
        }
      </ScrollView>
    </View>
  );
}

export const styles = StyleSheet.create({
  outer: {
    height: 42,
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignContent: 'center',
    alignItems: 'center',
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: theme.files.breadcrumbs.border,
    backgroundColor: theme.files.breadcrumbs.fill,
  },
  outerLarge: {
    height: 54,
  },
  root: {
    flex: 1,
    flexDirection: 'row',
    height: 42,
    marginLeft: 10,
  },
  rootLarge: {
    flex: 1,
    flexDirection: 'row',
    height: 54,
  },
  paths: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    height: 42,
  },
  pathsLarge: {
    height: 54,
    marginBottom: 0,
  },
  item: {
    alignItems: 'center',
    flexDirection: 'row',
    flexGrow: 1,
    flexShrink: 1,
  },
  itemNative: {
    flexBasis: 0,
  },
  text: {
    fontSize: 12,
    color: theme.files.breadcrumbs.text,
    paddingHorizontal: 8,
  },
  textLarge: {
    fontSize: 14,
  },
  textSpecial: {
    marginLeft: 6,
  },
  divider: {
    marginTop: 4,
  },
  iconWrapper: {
    minHeight: 32,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconHome: {
    marginLeft: 12,
  },
  iconHomeDepth: {
    marginRight: 12,
  },
  link: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignContent: 'center',
    alignItems: 'center',
  },
  textShare: {
    color: colors.neutral.mid,
    fontSize: 11,
  },
  buttonShare: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    marginRight: 14,
    marginBottom: 2,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: theme.files.breadcrumbs.shareBorder,
    backgroundColor: theme.files.breadcrumbs.share,
    shadowOffset: {width: 0, height: 1},
    shadowColor: 'rgba(0,0,0,0.2)',
  },
  buttonShareTouch: {
    paddingVertical: 6,
  },
});
